// TODO: Move this into /marketplace/src/checkout/actions, on-click-submit.test.js is located there
import _ from 'lodash';

import { checkPaysafeTokenFreshness } from 'src/checkout/components/sections/payment/paysafe-token-freshness';
import { PERSIST_CART_CALLER, persistCartData } from 'src/state/actions/cart';
import { getLogger } from 'src/utils/datadog-logger';

import validateSubmit from './validate-submit';
import validateCart from './validate-cart';
import buildOrder from './build-order';

const logger = getLogger('on-click-submit');

// called via src/checkout/components/confirmation/submit 48
// called via src/modals/kiosk-checkout/modal.jsx 90
export default async function onClickSubmit(
  submitOrder,
  {
    Cart,
    UI,
    User,
    apolloClient,
    showErnie,
    Checkout,
    checkOnDuty = false,
    isAnonymousCheckout = false,
    isEmailRequiredForCurbsideArrival = false,
    enableOmniauthLogin = false,
    tracker,
    flags,
    turnstileToken = '',
    considerPayerForIncentives = false,
    termsAccepted = null,
  }
) {
  const { isVariant, addErrors, saveMethods } = Checkout;
  // attempt to auto save specific sections
  try {
    // attempt to auto save sections that have registered themselves for saving
    await Promise.all(saveMethods.map((method) => method()));

    // old manually coded ones
    await Cart.medicalComponentRef?.current?.onSave();
    await Cart.orderTypeComponentRef?.current?.onSave();
    await Cart.scheduledOrderingRef?.current?.handleSave();
    // if we don't have a checkout token, persist cart to get one
    if (!Cart.checkoutToken) {
      await persistCartData(
        apolloClient,
        Cart,
        UI,
        User,
        flags,
        false,
        PERSIST_CART_CALLER.ON_CLICK_SUBMIT_LEGACY_EAGER
      );
    }
  } catch (e) {
    // log error to console--ernie logging is handled in respective onSave() calls above
    console.error(e);
    return { error: true, msg: e };
  }

  // ECOMM-9911: Abort if the Paysafe token is stale (form was unmounted before
  // submit, so the saveMethods autosave couldn't re-tokenize).
  const paysafeFreshnessAbort = checkPaysafeTokenFreshness({ Cart, flags, addErrors, logger });
  if (paysafeFreshnessAbort) return paysafeFreshnessAbort;

  // validate the cart
  const { proceed: cartIsValid, outOfStockItem, returnToMenu } = await validateCart({
    Cart,
    UI,
    apolloClient,
    showErnie,
    isVariant,
    checkOnDuty,
  });
  if (!cartIsValid) {
    return { error: true, outOfStockItem, returnToMenu };
  }

  // validate the order
  const { proceed: orderIsValid } = await validateSubmit({
    Cart,
    UI,
    User,
    showErnie,
    Checkout,
    isEmailRequiredForCurbsideArrival,
    enableOmniauthLogin,
    flags,
    turnstileToken,
  });

  if (!orderIsValid) {
    return { error: true };
  }
  // build the order
  const { order } = buildOrder({
    Cart,
    UI,
    User,
    Checkout,
    considerPayerForIncentives,
    termsAccepted,
    tipEligibilityValidationEnabled: flags?.['fintech.platform.validate-tip-eligibility.rollout'] ?? false,
  });
  order.isAnonymous = isAnonymousCheckout;

  // server side validations & attempt to submit the order
  const response = await submitOrder({ variables: { input: order, turnstileToken } });
  const valid = _.get(response, 'data.createOrderV2.valid', false);
  const serverErrors = _.get(response, 'data.createOrderV2.errors', false);
  if (!valid && !_.isEmpty(serverErrors)) {
    const dutchiePayError = serverErrors[0].extensions?.path === 'dutchiePay';
    const reasonCode = serverErrors[0].extensions?.reason_code;

    if (dutchiePayError) {
      tracker.paymentsError({
        reasonCode: reasonCode ?? 'NO_REASON_CODE',
        meta: serverErrors[0]?.extensions,
        surface: 'web',
        enhancedMessages: !!serverErrors[0]?.extensions?.consumer_message,
      });
    }

    addErrors(serverErrors);
    console.error({ 'Checkout Errors': serverErrors, Order: order });
  } else if (!valid) {
    console.error('CheckoutError', response);
    UI.showErnie('Error submitting order. Please contact customer support.');
  }

  return response;
}
